import { CSSProperties } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { MoreOptions, TextWithLineBreaks, User } from 'components/common';
import { ProfileCompetenciesEndorsementsDelete } from '.';

import type { MergedCompetency } from 'lib/competencies/getMergedCompetencies';
import { Box } from 'components/common/box';
import { EditedContent } from 'components/common/notifications';

interface Props {
  competency: MergedCompetency;
  style?: CSSProperties;
  updateCompetencies: () => Promise<void>;
}

const ProfileCompetenciesEndorsements = ({
  competency,
  style,
  updateCompetencies
}: Props) => {
  const { messages, theme, user } = useReduxState();

  const endorsements = competency.endorsements?.length
    ? competency.endorsements
    : competency.levels
    ? competency.levels.flatMap((level) => level.endorsements)
    : null;

  if (!endorsements?.length) return null;

  return (
    <div style={style}>
      <Box
        style={{ position: 'relative' }}
        padding={0}
        title={getMessageWithPlaceholders({
          message: messages.competenciesEndorsementsNumberOf,
          placeholders: { numberOfEndorsements: endorsements.length }
        })}
      >
        {endorsements.map((endorsement, index) => {
          // find the level this endorsement belongs to, if any
          const level = competency.levels?.find((level) =>
            level.endorsements.includes(endorsement)
          );
          return (
            <div className="endorsement" key={index}>
              <div className="header">
                <User
                  showEmailTooltip={true}
                  showEmail={false}
                  subtitle={
                    <EditedContent
                      createdAt={endorsement.createdAt}
                      updatedAt={endorsement.updatedAt}
                    />
                  }
                  user={endorsement.endorserUser}
                />

                {user?.roles.includes('superadmin') && (
                  <div className="delete-other-users-endorsement">
                    <MoreOptions renderAsFixed={true}>
                      <ProfileCompetenciesEndorsementsDelete
                        endorsement={endorsement}
                        updateCompetencies={updateCompetencies}
                      />
                    </MoreOptions>
                  </div>
                )}
              </div>

              {endorsement.comment && (
                <div className="comment">
                  <TextWithLineBreaks text={endorsement.comment} />
                </div>
              )}
              {level && competency.highestLevel.id !== level.id && (
                <div className="level-name">
                  {getMessageWithPlaceholders({
                    message: messages.competenciesEndorsementPreviousLevel,
                    placeholders: { levelName: level.name }
                  })}
                </div>
              )}
            </div>
          );
        })}
      </Box>

      <style jsx>{`
        .endorsement {
          padding: 15px;
        }
        .endorsement:not(:first-child) {
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .header {
          align-items: center;
          display: flex;
          gap: 20px;
          justify-content: space-between;
        }
        .comment {
          font-size: 1.5rem;
          font-weight: normal;
          line-height: 1.5;
          padding: 15px 0 0;
          word-break: break-word;
        }
        .level-name {
          color: ${theme.textColorLighter};
          margin-top: 15px;
          font-size: 1.4rem;
        }
      `}</style>
    </div>
  );
};

export default ProfileCompetenciesEndorsements;
